import { useEffect, useState } from 'react'
import {
  Alert,
  AlertColor,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import CancelRounded from '@mui/icons-material/CancelRounded'
import FsTabPanel from '../../components/common/atoms/TabPanel'
import FsDataTable from '../../components/common/molecules/DataTable'
import { WALKTHROUGH_STATUS } from '../../shared/constants'
import FSReportDrawer from '../../components/fs-reports/ReportDrawer'
import FsReportSpinner from '../../components/fs-reports/ReportSpinner'
import FsReportModal from '../../components/fs-reports/ReportModal'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { ConciseReport } from '../../shared/model/models'
import {
  changeWalkthroughIndex,
  changeWalkthroughStatus,
  deleteReport,
  setReports,
} from '../../store/actions'
import { reportAPI } from '../../shared/api/ReportAPI'
import FsDemoAccountModal from '../../components/common/molecules/DemoAccountModal'
import getTableColumns from '../../components/fs-reports/getTableColumns'
import FsReportEmptyState from '../../components/fs-reports/ReportsEmptyState'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'

const FsReportTable = () => {
  const { t } = useTranslation()
  const { walkthroughStatus, walkthroughIndex, currentTheme, currentLanguage, account } =
    useAppSelector((state) => state.currentUser)
  const { reports } = useAppSelector((state) => state.currentReport)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isConfirmAction, setIsConfirmAction] = useState<{
    reportId: number
    action: 'delete' | 'clone'
  } | null>(null)
  const [reportPoll, setReportPoll] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [rows, setRows] = useState<ConciseReport[]>([])
  const [reportState, setReportState] = useState<{ message: string; severity: AlertColor } | null>(
    null,
  )
  const [firstTimeLoad, setFirstTimeLoad] = useState<boolean>(true)
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)
  const [currentTab, setCurrentTab] = useState<'general' | 'legal'>('general')
  const [generationStatus, setGenerationStatus] = useState<string>('')

  const dispatch = useAppDispatch()
  const isDemoAccount = process.env.REACT_APP_SLUG === 'demo' && !account?.isSuperuser // getIsDemoAccount()

  const getReportsByType = (type: 'general' | 'legal') =>
    reports ? reports.filter((rep) => rep.reportType === type) : []

  const refreshReports = () => {
    if (reports) {
      const inProgressReports = reports.find((r) => r.pdfGenerationStatus === 'in_progress')
      if (inProgressReports) {
        const repPoll: any = setInterval(() => dispatch(setReports()), 20000)
        setReportPoll(repPoll)
      }
    }
  }

  useEffect(() => {
    const getReports = async () => {
      await dispatch(setReports())
      setFirstTimeLoad(false)
      refreshReports()
    }
    getReports()
  }, [])

  useEffect(() => {
    const inProgressReports = reports.find((r) => r.pdfGenerationStatus === 'in_progress')
    if (inProgressReports && !reportPoll) {
      refreshReports()
    }
    if (!inProgressReports && reportPoll) {
      clearInterval(reportPoll)
      setReportPoll(null)
    }
  }, [reports])

  useEffect(() => {
    setSearchTerm('')
    setRows(getReportsByType(currentTab))
  }, [currentTab, reports])

  useEffect(() => {
    if (walkthroughStatus === WALKTHROUGH_STATUS.Paused && walkthroughIndex === 9) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.InProgress))
      dispatch(changeWalkthroughIndex(10))
    }
  }, [walkthroughStatus, walkthroughIndex])

  const renameReport = async (reportId: number, name: string) => {
    try {
      const response = await reportAPI.renameReport(reportId, name)
      if (response === 200) {
        dispatch(setReports())
      }
    } catch (error: any) {
      setReportState({
        message: error.response.statusText,
        severity: 'error',
      })
    }
  }

  const processRowUpdate = (updatedRow: ConciseReport, previousRow: ConciseReport) => {
    if (updatedRow.reportName && updatedRow.reportName !== previousRow.reportName)
      renameReport(updatedRow.id, updatedRow.reportName)
    return updatedRow.reportName
      ? updatedRow
      : { ...updatedRow, reportName: previousRow.reportName }
  }

  const lockReport = async (reportId: number, isEditable: boolean) => {
    try {
      setIsLoading(true)
      const response = await reportAPI.lockReport(reportId, !isEditable)
      if (response === 200) {
        await dispatch(setReports())
      }
    } catch (error: any) {
      setReportState({
        message: error.response.statusText,
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const downloadReport = async (reportId: number, reportName: string) => {
    try {
      setIsLoading(true)
      const downloadURL = await reportAPI.getReportPDF(reportId)
      if (downloadURL.url) {
        const response = await axios({
          url: downloadURL.url,
          method: 'GET',
          responseType: 'blob',
          timeout: 180000,
        })
        if (response) {
          const blobUrl = URL.createObjectURL(response.data)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = `${reportName}.pdf`
          document.body.appendChild(link)
          link.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            }),
          )
          setTimeout(() => {
            document.body.removeChild(link)
          }, 100)
        }
      } else {
        await dispatch(setReports())
      }
    } catch (error: any) {
      setReportState({
        message: error.response.statusText,
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const cloneReport = async (reportId: number) => {
    setIsConfirmAction(null)
    try {
      setIsLoading(true)
      const response = await reportAPI.cloneReport(reportId)
      if (response === 200) {
        await dispatch(setReports())
      }
      setReportState({
        message: t('report.dulicate.success'),
        severity: 'success',
      })
    } catch (error: any) {
      setReportState({
        message: error.response.statusText,
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const deleteReportConfirmed = (reportId: number) => {
    dispatch(deleteReport({ reportId }))
  }

  const columns = getTableColumns(
    currentTab,
    setOpenDemoModal,
    setIsConfirmAction,
    downloadReport,
    lockReport,
  )

  const filterRowsBySearchTerm = () =>
    rows.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    )

  const tabs = [
    {
      label: <Typography variant='h3'>{t('general.reports')}</Typography>,
      value: 'general',
      component: null,
    },
    {
      label: <Typography variant='h3'>{t('legal.reports')}</Typography>,
      value: 'legal',
      component: null,
    },
  ]

  return (
    <>
      <FSReportDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        setGenerationStatus={(status: string) => setGenerationStatus(status)}
      />
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={!!reportState}
        onClose={() => setReportState(null)}
        autoHideDuration={2000}
      >
        <Alert
          onClose={() => setReportState(null)}
          severity={reportState?.severity}
          sx={{ width: '100%' }}
        >
          {reportState?.message}
        </Alert>
      </Snackbar>
      <Stack direction="row" alignItems="center" mb={3}>
        <Box mt={1}>{getIcon('reports')}</Box>
        <Typography variant="h1" ml={1} pr={2} mt={0.5}>
          {t('reports')}
        </Typography>
        <Button
          className="createreport"
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setDrawerOpen(true)
          }}
        >
          <Typography variant="body2">{t('generate')}</Typography>
        </Button>
      </Stack>

      {reports?.length > 0 ? (
        <>
          <Stack direction="row" mt={-1}>
            <FsTabPanel
              tabs={tabs}
              onClick={(tabValue: string) => setCurrentTab(tabValue as 'general' | 'legal')}
            />
            <Box sx={{ zIndex: 1 }}>
              <TextField
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ width: 300 }}
                placeholder={t('search')}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          onClick={() => setSearchTerm('')}
                          edge="end"
                        >
                          <CancelRounded />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
            </Box>
          </Stack>
          <FsDataTable
            columns={columns}
            rows={filterRowsBySearchTerm()}
            processRowUpdate={processRowUpdate}
            isCellEditable={(params) => params.row.isEditable && !isDemoAccount}
          />
        </>
      ) : (
        <FsReportEmptyState />
      )}

      {firstTimeLoad || isLoading ? (
        <FsFullPageLoader />
      ) : null}

      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} />
      <FsReportSpinner
        open={!!generationStatus}
        onClose={(isError: boolean) => {
          if (!isError) {
            setDrawerOpen(false)
          }
          setGenerationStatus('')
        }}
        generationStatus={generationStatus}
      />
      <Modal
        open={!!isConfirmAction}
        onClose={(_, reason) => {
          if (reason && reason === 'backdropClick') return
          setIsConfirmAction(null)
        }}
        disableEscapeKeyDown
      >
        <FsReportModal
          onClose={() => setIsConfirmAction(null)}
          confirmAction={isConfirmAction}
          cloneReport={cloneReport}
          deleteReport={deleteReportConfirmed}
        />
      </Modal>
    </>
  )
}
export default FsReportTable
